import { useEffect } from "react";
import { track } from "~/lib/analytics";

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;
const MS_PER_SECOND = 1000;
const MIN_TRACKED_SECONDS = 2;
/** Cap runaway timings (idle/backgrounded tabs) at 30 minutes. */
const MAX_TRACKED_SECONDS = 1800;

interface PageEngagementOptions {
  readonly pagePath: string;
}

/**
 * Track reading engagement on a page:
 * - `scroll_depth` fires once for each 25/50/75/100% threshold the visitor
 *   reaches (a page that fits the viewport counts as 100% immediately).
 * - `time_on_page` (seconds) fires when the visitor leaves the route or hides
 *   the tab, whichever comes first, ignoring sub-2s bounces.
 *
 * Consent-safe: `track` no-ops during SSR and until the consent manager loads
 * gtag.
 */
export function usePageEngagement({ pagePath }: PageEngagementOptions): void {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const start = performance.now();
    const firedThresholds = new Set<number>();
    let timeReported = false;

    const computeDepth = (): number => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) {
        return 100;
      }
      return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
    };

    const handleScroll = (): void => {
      const depth = computeDepth();
      for (const threshold of SCROLL_THRESHOLDS) {
        if (depth >= threshold && !firedThresholds.has(threshold)) {
          firedThresholds.add(threshold);
          track("scroll_depth", { page_path: pagePath, percent: threshold });
        }
      }
    };

    const reportTime = (): void => {
      if (timeReported) {
        return;
      }
      const seconds = Math.round((performance.now() - start) / MS_PER_SECOND);
      if (seconds < MIN_TRACKED_SECONDS) {
        return;
      }
      timeReported = true;
      track("time_on_page", {
        page_path: pagePath,
        seconds: Math.min(seconds, MAX_TRACKED_SECONDS),
      });
    };

    const handleVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        reportTime();
      }
    };

    // Account for pages that already fit within the viewport on load.
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", reportTime);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", reportTime);
      reportTime();
    };
  }, [pagePath]);
}
